import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CardComponent } from '@app/components/card';
import { FormActionButtonComponent } from '@app/components/form-action-button';
import { InputComponent } from '@app/components/input';
import {
  OptionsObj,
  SelectDropdownComponent,
} from '@app/components/select-dropdown';
import { SpinnerComponent } from '@app/components/spinner';
import { TextComponent } from '@app/components/text';
import { TitleComponent } from '@app/components/title-section';
import { ToggleComponent } from '@app/components/toggle';
import {
  Challenge,
  ChallengeService,
  STEP_CHALLENGE_TEAM_ASSIGNMENT_OPTIONS,
  StepChallengeTeamAssignmentType,
  hasChallengeDayArrived,
} from '@app/services/challenge/challenge.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatePipe } from '@ngx-translate/core';
import { filter, merge } from 'rxjs';

export interface StepChallengeFormValue {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  enrollmentStartDate: string;
  goal: string;
  joinInProgress: boolean;
  teamAssignmentType: StepChallengeTeamAssignmentType;
  teamAssignmentValue: number | null;
}

export interface StepChallengeSaveEvent {
  value: StepChallengeFormValue;
  changedFields: Partial<StepChallengeFormValue>;
}

@Component({
  selector: 'app-step-challenge-form',
  templateUrl: './step-challenge-form.component.html',
  styleUrl: './step-challenge-form.component.css',
  standalone: true,
  imports: [
    CardComponent,
    FormActionButtonComponent,
    InputComponent,
    ReactiveFormsModule,
    SpinnerComponent,
    TextComponent,
    TitleComponent,
    ToggleComponent,
    TranslatePipe,
    SelectDropdownComponent,
  ],
})
export class StepChallengeFormComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly challengeService = inject(ChallengeService);
  private readonly saveChanges = inject(SaveChangesService);

  initialValue = input<Challenge | undefined>(undefined);
  isReadOnly = input(false);
  isSaving = input(false);
  goBackNavigate = input<string[]>(['challenges']);

  save = output<StepChallengeSaveEvent>();
  editRequested = output<void>();
  back = output<void>();

  isCanJoinAfterStart = signal(true);
  teamAssignmentType = signal(StepChallengeTeamAssignmentType.DEFAULT);
  isSignupDateLocked = computed(() =>
    this.hasDateArrived(this.initialValue()?.enrollmentStartDate)
  );
  isStartDateLocked = computed(() =>
    this.hasDateArrived(this.initialValue()?.startDate)
  );
  errors = signal({
    duration: '',
    title: '',
    description: '',
    signupDate: '',
    startDate: '',
    allowAutoJoin: '',
    numberOfTeams: '',
    maxTeamNumber: '',
  });

  stepChallengeForm = new FormGroup({
    title: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    duration: new FormControl(1, [Validators.required]),
    durationUnit: new FormControl(1, [Validators.required]),
    dailyStepLimit: new FormControl('unlimited', [Validators.required]),
    endDate: new FormControl<string | null>(null, [Validators.required]),
    signupDate: new FormControl<string | null>(null, [Validators.required]),
    startDate: new FormControl<string | null>(null, [Validators.required]),
    numberOfTeams: new FormControl('', [
      this.checkIfRequire(
        () =>
          this.teamAssignmentType() !==
          StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
      ),
      Validators.min(2),
      Validators.max(10),
    ]),
    maxTeamNumber: new FormControl('', [
      this.checkIfRequire(
        () =>
          this.teamAssignmentType() !==
          StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
      ),
      Validators.min(3),
    ]),
  });

  durationUnits = [
    { label: 'Days', value: 1 },
    { label: 'Weeks', value: 7 },
  ];

  dailyStepLimitOptions: OptionsObj[] = [
    { label: 'Unlimited', value: 'unlimited' },
    { label: '1000', value: '1k/day' },
    { label: '2500', value: '2.5k/day' },
    { label: '5000', value: '5k/day' },
    { label: '7500', value: '7.5k/day' },
    { label: '10000', value: '10k/day' },
  ];

  currentDate = new Date();
  private savedTeamAssignmentType = StepChallengeTeamAssignmentType.DEFAULT;

  stepChallengeTeamAssignmentType = StepChallengeTeamAssignmentType;
  readonly teamAssignmentOptions = STEP_CHALLENGE_TEAM_ASSIGNMENT_OPTIONS;

  constructor() {
    toObservable(this.initialValue)
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe((challenge) => this.populateForm(challenge));
  }

  ngOnInit(): void {
    this.saveChanges.setHasChanges(false);

    (
      [
        'title',
        'description',
        'duration',
        'signupDate',
        'startDate',
        'numberOfTeams',
        'maxTeamNumber',
      ] as const
    ).forEach((field) => {
      this.stepChallengeForm
        .get(field)
        ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.clearFieldError(field));
    });

    this.stepChallengeForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveChanges.setHasChanges(this.stepChallengeForm.dirty);
      });

    merge(
      this.stepChallengeForm.get('startDate')!.valueChanges,
      this.stepChallengeForm.get('duration')!.valueChanges,
      this.stepChallengeForm.get('durationUnit')!.valueChanges
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const startDate = this.stepChallengeForm.get('startDate')!.value;
        const duration = this.stepChallengeForm.get('duration')!.value;
        const durationUnit = this.stepChallengeForm.get('durationUnit')!.value;

        if (!startDate || !duration || !durationUnit) return;

        this.stepChallengeForm.patchValue(
          { endDate: this.createEndDate(startDate, duration, durationUnit) },
          { emitEvent: false }
        );
      });
  }

  handleCheck() {
    this.isCanJoinAfterStart.set(!this.isCanJoinAfterStart());
    this.stepChallengeForm.markAsDirty();
    this.saveChanges.setHasChanges(this.stepChallengeForm.dirty);
  }

  onSelectTeamAssignment(value: StepChallengeTeamAssignmentType) {
    this.teamAssignmentType.set(value);
    this.stepChallengeForm.get('numberOfTeams')?.updateValueAndValidity();
    this.stepChallengeForm.get('maxTeamNumber')?.updateValueAndValidity();
    this.updateNumberOfTeamsErrors();
    this.updateMaxNumberOfTeamsErrors();
    this.stepChallengeForm.markAsDirty();
    this.saveChanges.setHasChanges(this.stepChallengeForm.dirty);
  }

  getAssignmentDataTestId(type: StepChallengeTeamAssignmentType): string {
    return `step-challenges-team-assignment-option-${type}-toggle`;
  }

  onSave() {
    if (this.stepChallengeForm.invalid) {
      this.stepChallengeForm.markAllAsTouched();
      this.updateFieldErrors();
      return;
    }
    const value = this.buildFormValue();
    this.save.emit({ value, changedFields: this.getChangedFields(value) });
  }

  onEditRequested() {
    this.editRequested.emit();
  }

  onBack() {
    this.back.emit();
  }

  private buildFormValue(): StepChallengeFormValue {
    const startDate = this.stepChallengeForm.get('startDate')?.value ?? '';
    const duration = this.stepChallengeForm.get('duration')?.value ?? 1;
    const durationUnit = this.stepChallengeForm.get('durationUnit')?.value ?? 1;
    return {
      title: this.stepChallengeForm.get('title')?.value ?? '',
      description: this.stepChallengeForm.get('description')?.value ?? '',
      startDate,
      endDate: this.createEndDate(startDate, duration, durationUnit),
      enrollmentStartDate:
        this.stepChallengeForm.get('signupDate')?.value ?? '',
      goal: this.stepChallengeForm.get('dailyStepLimit')?.value ?? '',
      joinInProgress: this.isCanJoinAfterStart(),
      teamAssignmentType: this.teamAssignmentType(),
      teamAssignmentValue: this.getTeamAssignmentValue(),
    };
  }

  private getChangedFields(
    value: StepChallengeFormValue
  ): Partial<StepChallengeFormValue> {
    const initial = this.initialValue();
    if (!initial) {
      return {};
    }

    const controls = this.stepChallengeForm.controls;
    const changed: Partial<StepChallengeFormValue> = {};

    if (controls.title.dirty) changed.title = value.title;
    if (controls.description.dirty) changed.description = value.description;
    if (controls.dailyStepLimit.dirty) changed.goal = value.goal;
    if (controls.signupDate.dirty) {
      changed.enrollmentStartDate = value.enrollmentStartDate;
    }
    if (
      controls.startDate.dirty ||
      controls.duration.dirty ||
      controls.durationUnit.dirty
    ) {
      changed.startDate = value.startDate;
      changed.endDate = value.endDate;
    }
    if (value.joinInProgress !== !!initial.joinInProgress) {
      changed.joinInProgress = value.joinInProgress;
    }
    if (
      value.teamAssignmentType !== this.savedTeamAssignmentType ||
      controls.numberOfTeams.dirty ||
      controls.maxTeamNumber.dirty
    ) {
      changed.teamAssignmentType = value.teamAssignmentType;
      changed.teamAssignmentValue = value.teamAssignmentValue;
    }
    return changed;
  }

  private hasDateArrived(date?: string): boolean {
    return !!date && hasChallengeDayArrived(new Date(date), this.currentDate);
  }

  private populateForm(challenge: Challenge): void {
    const startDate = this.toDateString(challenge.startDate);
    const endDate = this.toDateString(challenge.endDate);
    const defaultDuration = 1;
    const defaultDurationUnit = 1;
    const [duration, durationUnit] =
      startDate && endDate
        ? this.challengeService.dateToDurationUnit(startDate, endDate)
        : [defaultDuration, defaultDurationUnit];

    this.isCanJoinAfterStart.set(!!challenge.joinInProgress);

    if (
      challenge.teamAssignmentType ===
      StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
    ) {
      this.teamAssignmentType.set(
        StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
      );
    } else if (
      challenge.teamAssignmentType ===
      StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
    ) {
      this.teamAssignmentType.set(
        StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
      );
    } else {
      this.teamAssignmentType.set(StepChallengeTeamAssignmentType.DEFAULT);
    }
    this.savedTeamAssignmentType = this.teamAssignmentType();

    this.stepChallengeForm.patchValue(
      {
        title: challenge.name ?? '',
        description: challenge.description ?? '',
        duration,
        durationUnit,
        dailyStepLimit: challenge.goal ?? 'unlimited',
        signupDate: this.toDateString(challenge.enrollmentStartDate),
        startDate,
        endDate,
        numberOfTeams:
          challenge.teamAssignmentType ===
          StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
            ? String(challenge.teamAssignmentValue ?? '')
            : '',
        maxTeamNumber:
          challenge.teamAssignmentType ===
          StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
            ? String(challenge.teamAssignmentValue ?? '')
            : '',
      },
      { emitEvent: false }
    );
    this.stepChallengeForm.markAsPristine();
  }

  private createEndDate = (
    startDate: string,
    duration: number,
    durationUnit: number
  ) => {
    const start = new Date(startDate);
    if (Number.isNaN(start.getTime())) {
      return '';
    }
    const dayOffset = 1;
    start.setUTCDate(
      start.getUTCDate() + Math.max(0, duration * durationUnit - dayOffset)
    );
    return start.toISOString().split('T')[0];
  };

  private toDateString(date?: string): string | null {
    if (!date) return null;
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime())
      ? null
      : parsed.toISOString().split('T')[0];
  }

  private getTeamAssignmentValue(): number | null {
    if (
      this.teamAssignmentType() ===
      StepChallengeTeamAssignmentType.FIXED_TEAM_COUNT
    ) {
      return Number(this.stepChallengeForm.get('numberOfTeams')?.value);
    }
    if (
      this.teamAssignmentType() ===
      StepChallengeTeamAssignmentType.MAX_TEAM_SIZE
    ) {
      return Number(this.stepChallengeForm.get('maxTeamNumber')?.value);
    }
    return null;
  }

  private updateFieldErrors(): void {
    this.errors.update((current) => ({
      ...current,
      duration: this.stepChallengeForm.get('duration')?.invalid
        ? 'Duration is required'
        : '',
      title: this.stepChallengeForm.get('title')?.invalid
        ? 'Title is required'
        : '',
      description: this.stepChallengeForm.get('description')?.invalid
        ? 'Description is required'
        : '',
      signupDate: this.stepChallengeForm.get('signupDate')?.invalid
        ? 'Signup date is required'
        : '',
      startDate: this.stepChallengeForm.get('startDate')?.invalid
        ? 'Start date is required'
        : '',
    }));
    this.updateNumberOfTeamsErrors();
    this.updateMaxNumberOfTeamsErrors();
  }

  private clearFieldError(
    field:
      | 'duration'
      | 'title'
      | 'description'
      | 'signupDate'
      | 'startDate'
      | 'numberOfTeams'
      | 'maxTeamNumber'
  ): void {
    if (field === 'numberOfTeams') {
      this.updateNumberOfTeamsErrors();
      return;
    }
    if (field === 'maxTeamNumber') {
      this.updateMaxNumberOfTeamsErrors();
      return;
    }
    if (this.stepChallengeForm.get(field)?.valid) {
      this.errors.update((current) => ({ ...current, [field]: '' }));
    }
  }

  private checkIfRequire(skipWhen: () => boolean): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (skipWhen()) return null;
      if (control.value == null || control.value === '')
        return { required: true };
      return null;
    };
  }

  private updateNumberOfTeamsErrors(): void {
    const control = this.stepChallengeForm.get('numberOfTeams');
    if (control?.errors) {
      if (control.errors['required']) {
        this.errors.update((c) => ({
          ...c,
          numberOfTeams: 'Number of teams is required',
        }));
      } else if (control.errors['min']) {
        this.errors.update((c) => ({
          ...c,
          numberOfTeams: `Number of teams must be at least ${
            control.errors!['min'].min
          }.`,
        }));
      } else if (control.errors['max']) {
        this.errors.update((c) => ({
          ...c,
          numberOfTeams: `Number of teams must be less than ${
            control.errors!['max'].max + 1
          }.`,
        }));
      }
    } else {
      this.errors.update((c) => ({ ...c, numberOfTeams: '' }));
    }
  }

  private updateMaxNumberOfTeamsErrors(): void {
    const control = this.stepChallengeForm.get('maxTeamNumber');
    if (control?.errors) {
      if (control.errors['required']) {
        this.errors.update((c) => ({
          ...c,
          maxTeamNumber: 'Max users per team is required',
        }));
      } else if (control.errors['min']) {
        this.errors.update((c) => ({
          ...c,
          maxTeamNumber: `Number of users must be at least ${
            control.errors!['min'].min
          }.`,
        }));
      }
    } else {
      this.errors.update((c) => ({ ...c, maxTeamNumber: '' }));
    }
  }
}
